import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { useTranslation } from "react-i18next";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import * as yup from "yup";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import moment from "moment";
import { InventoryPermissions } from "src/constants/permissions";
import {
  convertObjectToFormData,
  specificBranchHasPermission,
} from "src/utils";
import { DatePicker } from "@mui/x-date-pickers";
import {
  useGetBranches,
  useGetCurrencies,
  useGetPaymentMethods,
} from "src/api/generic";
import { useGetClientsList } from "src/api/sales/clients";
import { useGetMenuProducts } from "src/api/inventory/menu";
import {
  useAddInvoiceMutation,
  useCancelAndCopyInvoiceMutation,
  useGetInvoiceById,
  useGetTermsList,
  useUpdateInvoiceMutation,
} from "src/api/sales/invoices";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import AddMoreIcon from "src/assets/svg-icons/settings/add_more.svg";
import { authState } from "src/store/auth";

const schema = yup.object().shape({
  branch_id: yup.number().required("Required"),
  client_id: yup.number().required("Required"),
  date: yup.date().required("Required"),
  invoice_date: yup.date().required("Required"),
  currency_id: yup.number().required("Required"),
  discount: yup.string().required("Required"),
  is_discount_percentage: yup.string().required("Required"),
  note: yup.string().nullable(),
  term_id: yup.string().required("Required"),

  items: yup
    .array()
    .of(
      yup.object().shape({
        item_id: yup.number(),
        menu_name: yup.string().required("Required"),
        menu_id: yup.string().required("Required"),
        branch_id: yup.string().required("Required"),
        unit_cost: yup.string().required("Required"),
        tax_rate: yup.string().required("Required"),
        quantity: yup.string().required("Required"),
        discount_percentage: yup.string().required("Required"),
        price: yup.string().required("Required"),
      }),
    )
    .min(1, "At least one item is required")
    .required("Required"),

  payments: yup
    .array()
    .of(
      yup.object().shape({
        payment_method_id: yup
          .string()
          .test(
            "payment-method-required",
            "Payment method is required when amount is specified",
            function (value) {
              const { amount } = this.parent;
              if (amount && !value) {
                return false;
              }
              return true;
            },
          ),
        amount: yup
          .string()
          .test(
            "payment-amount-required",
            "Payment amount is required when payment method is selected",
            function (value) {
              const { payment_method_id } = this.parent;
              if (payment_method_id && !value) {
                return false;
              }
              return true;
            },
          )
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value) > 0;
          }),
      }),
    )
    .required("Required"),

  files: yup.array().of(yup.mixed()).nullable(),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

const AddEditInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("inventory");
  const { t: salesT } = useTranslation("addSales");
  const { t: purchasingT } = useTranslation("purchasing");
  const { id } = useParams();
  const [auth, setAuth] = useRecoilState(authState);
  const { user } = auth;
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(true);
  const [files, setFiles] = useState<any>([]);
  const [saveAsDraft, setSaveAsDraft] = useState<boolean>(false);

  const isToCancelAndCopy = location.pathname.includes("/copy");
  const isToAddCreditNote = location.pathname.includes("/add-credit-note");
  const mode = !!id && !isToCancelAndCopy ? "edit" : "add";

  const HeadText = [
    generalT("item"),
    generalT("inventory"),
    salesT("taxRate"),
    generalT("qty"),
    generalT("price"),
    t("amount"),
    generalT("discount"),
    generalT("tax"),
    generalT("total"),
    generalT("actions"),
  ];

  // APIS
  const {
    data: invoiceData,
    status: getCountByIdStatus,
    refetch,
    isFetching,
    isLoading,
  } = useGetInvoiceById({
    enabled: !!id,
    id: id as string,
  });
  const { data: itemsData } = useGetMenuProducts({ page: 1 });
  const { data: branchesData } = useGetBranches();
  const { data: clientsData } = useGetClientsList();
  const { data: currenciesData } = useGetCurrencies();
  const { data: termsData } = useGetTermsList();
  const { mutate, error, status, isPending, data } = useAddInvoiceMutation();
  const {
    mutate: mutateUpdate,
    error: errorUpdate,
    status: statusUpdate,
    isPending: isPendingUpdate,
  } = useUpdateInvoiceMutation();
  const {
    mutate: mutateCancelAndCopy,
    error: errorCancelAndCopy,
    status: statusCancelAndCopy,
    isPending: isPendingCancelAndCopy,
  } = useCancelAndCopyInvoiceMutation();
  const { data: paymentsData } = useGetPaymentMethods();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    watch,
  } = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      discount: "0",
      is_discount_percentage: "1",
      note: "",
      payments: [
        {
          payment_method_id: "",
          amount: "",
        },
      ],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "items",
    control,
  });

  const {
    fields: paymentsFields,
    append: appendPayment,
    remove: removePayment,
  } = useFieldArray({
    name: "payments",
    control,
  });

  const isCreditNote = invoiceData?.data?.type === "credit_note";

  const selectedClient = clientsData?.data.find((client: any) => {
    return client.id === +watch("client_id");
  });

  const mandatoryTerms =
    termsData?.data.filter((term) => term?.is_mandatory) ?? [];
  const optionalTerms =
    termsData?.data.filter((term) => !term?.is_mandatory) ?? [];

  // permissions
  const confirmPermission = specificBranchHasPermission(
    InventoryPermissions.confirmStocktaking,
    invoiceData?.data.branch.id,
  );
  const addPermission = specificBranchHasPermission(
    InventoryPermissions.addStocktaking,
    invoiceData?.data.branch.id,
  );

  const branchesHavePermission =
    branchesData?.data.filter(
      (branch) =>
        branch.status &&
        specificBranchHasPermission(
          InventoryPermissions.addStocktaking,
          branch.id,
        ),
    ) || [];

  let subTotal = 0;
  let allTax = 0;

  watch("items")?.forEach((item) => {
    const amount = (Number(item.quantity) || 0) * (Number(item.price) || 0);
    const amountAfterDiscount =
      amount * (1 - (Number(item.discount_percentage) / 100 || 0));
    subTotal += amountAfterDiscount;

    const tax = amountAfterDiscount * (Number(item.tax_rate) / 100 || 0);
    allTax += tax;
  });

  const allTotal = subTotal + allTax;

  const allDiscount =
    watch("is_discount_percentage") === "1"
      ? (Number(watch("discount")) / 100) * subTotal || 0
      : (Number(watch("discount")) ?? 0);

  const allTotalAfterDiscount = allTotal - allDiscount;

  const navigateToInvoices = () => {
    navigate("/sales/invoices");
  };

  // Handler function when item is selected
  const handleItemSelect = (event: React.SyntheticEvent, newValue: any) => {
    if (!validateLockDate()) return;
    console.log({ newValue });
    setSelectedItem(newValue);
    if (newValue) {
      append({
        menu_name: newValue?.name,
        menu_id: newValue?.id,
        branch_id: "",
        unit_cost: newValue.unit_cost ?? 0,
        tax_rate: "",
        quantity: "",
        discount_percentage: "",
        price: "",
      });

      setInputValue("");
      setSelectedItem(null);
    }
  };

  // Handle manual changes to the input field
  const handleInputChange = (
    event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    if (selectedItem === null) {
      setInputValue(newInputValue);
    }
  };

  const validateLockDate = (): boolean => {
    if (
      !!invoiceData?.data?.branch?.close_date &&
      moment(watch("date"))
        .startOf("day")
        .isSameOrBefore(
          moment(invoiceData?.data?.branch?.close_date).startOf("day"),
        )
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("canNotAddEditOrDeleteBeforeLockDate")}: ${
            invoiceData?.data?.branch?.close_date
          }`,
        },
      ]);
      return false;
    }
    return true;
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
  };

  const handleAppendPayment = () => {
    appendPayment({
      payment_method_id: "",
      amount: "",
    });
  };

  const handleRemovePayment = (index: number) => {
    removePayment(index);
  };

  const selectedTerm = optionalTerms?.find(
    (term) => term.id === watch("term_id"),
  );

  console.log({ selectedTerm });

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    // Lock date validation
    if (!validateLockDate()) return;

    const { items, payments, date, invoice_date, ...rest } = data;

    const termsTitles = optionalTerms?.map(({ title }) => title);

    if (selectedTerm) {
      termsTitles?.push(selectedTerm?.title);
    }

    const dataToSendWithoutArrays = {
      ...rest,
      type: isToAddCreditNote || isCreditNote ? "credit_note" : "invoice",
      amount: subTotal,
      discount: allDiscount ?? 0,
      total: allTotalAfterDiscount,
      tax: allTax,
      note: "note",
      invoice_status: saveAsDraft ? "draft" : "submitted",
      date: moment(date).format("YYYY-MM-DD"),
      invoice_date: moment(invoice_date).format("YYYY-MM-DD"),
      // zatca_status: "pending",
    };

    console.log({ saveAsDraft });

    if (mode === "add") {
      const formDataToSend = convertObjectToFormData(dataToSendWithoutArrays);

      // append items
      if (items.length > 0) {
        items.forEach((item, index: number) => {
          const amount =
            (Number(item.quantity) || 0) * (Number(item.price) || 0);
          const discount =
            amount * (Number(item.discount_percentage) / 100 || 0);
          const amountAfterDiscount =
            amount * (1 - (Number(item.discount_percentage) / 100 || 0));
          const tax = amountAfterDiscount * (Number(item.tax_rate) / 100 || 0);

          const total = amountAfterDiscount + tax;

          formDataToSend.append(`items[${index}][menu_id]`, item.menu_id);
          formDataToSend.append(`items[${index}][branch_id]`, item.branch_id);
          formDataToSend.append(`items[${index}][unit_cost]`, item.unit_cost);
          formDataToSend.append(`items[${index}][tax_rate]`, item.tax_rate);
          formDataToSend.append(`items[${index}][quantity]`, item.quantity);
          formDataToSend.append(
            `items[${index}][discount_percentage]`,
            item.discount_percentage,
          );
          formDataToSend.append(
            `items[${index}][discount_amount]`,
            `${discount}`,
          );
          formDataToSend.append(`items[${index}][price]`, item.price);
          formDataToSend.append(`items[${index}][amount]`, `${amount}`);

          formDataToSend.append(`items[${index}][tax]`, `${tax}`);
          formDataToSend.append(`items[${index}][total]`, `${total}`);
        });
      }

      // append payments array
      payments.forEach((payment, index: number) => {
        if (
          payment.payment_method_id &&
          payment.amount &&
          +payment?.amount > 0
        ) {
          formDataToSend.append(
            `payments[${index}][payment_method_id]`,
            payment.payment_method_id,
          );
          formDataToSend.append(`payments[${index}][amount]`, payment.amount);
        }
      });

      // append files
      if (files.length > 0) {
        files.forEach((file: any, index: number) => {
          formDataToSend.append(`files[${index}]`, file);
        });
      }

      // append terms
      if (termsTitles?.length > 0) {
        termsTitles.forEach((title, index: number) => {
          formDataToSend.append(`terms[${index}]`, title);
        });
      }

      if (isToCancelAndCopy) {
        mutateCancelAndCopy({
          id: id as string,
          invoiceFormData: formDataToSend,
        });
      } else {
        mutate(formDataToSend);
      }
    } else {
      mutateUpdate({
        id,
        ...dataToSendWithoutArrays,
        payments: payments,
        terms: termsTitles,
        items: items.map((item) => {
          const amount =
            (Number(item.quantity) || 0) * (Number(item.price) || 0);
          const discount =
            amount * (Number(item.discount_percentage) / 100 || 0);
          const amountAfterDiscount =
            amount * (1 - (Number(item.discount_percentage) / 100 || 0));
          const tax = amountAfterDiscount * (Number(item.tax_rate) / 100 || 0);

          const total = amountAfterDiscount + tax;

          return {
            branch_id: item.branch_id,
            menu_id: item.menu_id,
            unit_cost: item.unit_cost,
            price: item.price,
            tax_rate: item.tax_rate,
            quantity: item.quantity,
            discount_percentage: item.discount_percentage,
            discount_amount: discount,
            amount: amount,
            tax: tax,
            total: total,
          };
        }),
      });
    }
  };

  useEffect(() => {
    if (getCountByIdStatus === "success") {
      console.log({ invoiceData });
      if (isSaving && !isFetching) {
        console.log("resetting", { invoiceData });
        reset({
          branch_id: invoiceData.data?.branch?.id,
          client_id: invoiceData.data?.client?.id,
          currency_id: invoiceData.data?.currency?.id,
          discount: invoiceData.data.discount,
          is_discount_percentage: invoiceData.data.is_discount_percentage
            ? "1"
            : "0",
          term_id: invoiceData.data.term_id,
          note: invoiceData.data.note ?? "",
          date: moment(invoiceData.data.date).toDate(),
          invoice_date: moment(invoiceData.data.invoice_date).toDate(),
          items: invoiceData.data.items.map((item: any) => {
            return {
              branch_id: item.branch_id,
              menu_name: item.menu?.name,
              menu_id: item.menu?.id,
              unit_cost: item.menu?.unit_cost,
              price: item.price,
              tax_rate: item.tax_rate,
              quantity: item.quantity,
              discount_percentage: item.discount_percentage,
            };
          }),
          payments: invoiceData.data?.payments.map((pay: any) => {
            return {
              payment_method_id: pay.payment_method?.id,
              amount: pay.total,
            };
          }),
        });
        setIsSaving(false);
      }
    }
  }, [getCountByIdStatus, isFetching]);

  //Update success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  //Add success and error handling
  useEffect(() => {
    if (status === "success" || statusCancelAndCopy === "success") {
      navigate(
        status === "success"
          ? `/sales/invoices/${data?.data.sale_invoice_id}`
          : "/sales/invoices",
      );
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error" || statusCancelAndCopy === "error") {
      const errorReceived = status === "error" ? error : errorCancelAndCopy;
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorReceived?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status, statusCancelAndCopy]);

  let paymentsCredit = 0;
  watch("payments").forEach((pay) => {
    paymentsCredit += Number(pay.amount) || 0;
  });

  const credit = allTotalAfterDiscount - paymentsCredit || 0;

  const selectedItemsIds = watch("items")?.map((item) => item.item_id);

  console.log({ errors });

  if (isFetching) {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        height={"500px"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      {/* <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={InventoryIcon} width={"32px"} alt="" />
        </Box>
        <Typography fontSize={"32px"} lineHeight={"38.4px"}>
          {t("inventory")}
        </Typography>
      </Stack> */}
      {/* divider */}
      {/* <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} /> */}

      <Stack spacing={2} direction={"row"} mb={3} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInvoices}
          sx={{ cursor: "pointer" }}
        >
          {generalT("sales")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToInvoices}
          sx={{ cursor: "pointer" }}
        >
          {salesT("salesInvoices")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {mode === "add"
              ? salesT("addNewSalesInvoice")
              : invoiceData?.data.reference}
          </Typography>
        </Box>
      </Stack>

      {isLoading ? (
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          minHeight={"250px"}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack gap={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack direction={"row"} gap={1} mb={2} alignItems={"center"}>
                    <Typography>{salesT("billedBy")}</Typography>
                    <Typography fontSize={"13px"} color={"textSecondary"}>
                      {salesT("yourBusinessDetails")}
                    </Typography>
                  </Stack>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.branch_id}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {generalT("branch")}
                    </InputLabel>
                    <Controller
                      name="branch_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={generalT("branch")}
                        >
                          {branchesHavePermission?.map(
                            ({ id, name, active_subscriptions }) => {
                              return (
                                <MenuItem
                                  key={id}
                                  value={`${id}`}
                                  disabled={
                                    !active_subscriptions?.package.has_items
                                  }
                                >
                                  {name}
                                </MenuItem>
                              );
                            },
                          )}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {generalT(`${errors.branch_id?.message ?? ""}`)}
                    </FormHelperText>
                  </FormControl>
                  {!!watch("branch_id") && (
                    <Stack
                      p={2}
                      gap={1.5}
                      bgcolor={"#F9FAFB"}
                      borderRadius={"6px"}
                      border={"1px solid #dee3e8"}
                      mt={1}
                    >
                      <Typography
                        color={"#344054"}
                        fontWeight={600}
                        fontSize={"14px"}
                      >
                        {salesT("yourBusinessDetails")}
                      </Typography>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("name")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {user.company?.name}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("email")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {user.email}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("phone")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {user.phone}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("vat")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {""}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction={"row"} gap={1} mb={2} alignItems={"center"}>
                    <Typography>{salesT("billedTo")}</Typography>
                    <Typography fontSize={"13px"} color={"textSecondary"}>
                      {salesT("clientDetails")}
                    </Typography>
                  </Stack>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.client_id}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {salesT("client")}
                    </InputLabel>
                    <Controller
                      name="client_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={salesT("client")}
                        >
                          {clientsData?.data?.map(({ id, name }: any) => {
                            return (
                              <MenuItem key={id} value={id}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {generalT(`${errors.client_id?.message ?? ""}`)}
                    </FormHelperText>
                  </FormControl>
                  {selectedClient && (
                    <Stack
                      p={2}
                      gap={1.5}
                      bgcolor={"#F9FAFB"}
                      borderRadius={"6px"}
                      border={"1px solid #dee3e8"}
                      mt={1}
                    >
                      <Typography
                        color={"#344054"}
                        fontWeight={600}
                        fontSize={"14px"}
                      >
                        {salesT("clientDetails")}
                      </Typography>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("name")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {selectedClient?.name}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("email")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {selectedClient?.email}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("phone")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {selectedClient?.phone}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {generalT("vat")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {""}
                        </Typography>
                      </Stack>
                      <Stack direction={"row"} justifyContent={"space-between"}>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {salesT("clientBalance")}
                        </Typography>
                        <Typography fontSize={"14px"} color={"#344054"}>
                          {selectedClient?.balance.toFixed(2) ?? 0} SAR
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="invoice_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.invoice_date,
                            helperText: t(
                              `${errors.invoice_date?.message ?? ""}`,
                            ),
                            size: "small",
                            label: purchasingT("invoiceDate"),
                          },
                        }}
                        format={"dd/MM/yyyy"}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.date,
                            helperText: t(`${errors.date?.message ?? ""}`),
                            size: "small",
                            label: purchasingT("dueDate"),
                          },
                        }}
                        format={"dd/MM/yyyy"}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.currency_id}
                  >
                    <InputLabel id="demo-simple-select-label">
                      {salesT("currency")}
                    </InputLabel>
                    <Controller
                      name="currency_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          key={field.value}
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          label={salesT("currency")}
                        >
                          {currenciesData?.data?.map(({ id, name }: any) => {
                            return (
                              <MenuItem key={id} value={id}>
                                {name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      )}
                    />
                    <FormHelperText id="my-helper-text">
                      {generalT(`${errors.currency_id?.message ?? ""}`)}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography>{salesT("invoiceItems")}</Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {HeadText.map((item) => (
                        <TableCell
                          key={item}
                          align="center"
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: "18px",
                            color: "#fff",
                            bgcolor: "#FF8D3F",
                          }}
                        >
                          {item}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((itemField, index) => {
                      const amount =
                        (Number(watch(`items.${index}`).quantity) || 0) *
                        (Number(watch(`items.${index}`).price) || 0);
                      const amountDiscounted =
                        amount *
                        (1 -
                          (Number(watch(`items.${index}`).discount_percentage) /
                            100 || 0));
                      const tax =
                        amountDiscounted *
                        (Number(watch(`items.${index}`).tax_rate) / 100 || 0);

                      const total = amountDiscounted + tax;

                      return (
                        <TableRow
                          key={itemField.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center">
                            {itemField?.menu_name}
                          </TableCell>
                          {/* branch */}
                          <TableCell align="center">
                            <FormControl
                              fullWidth
                              size="small"
                              error={!!errors.items?.[index]?.branch_id}
                            >
                              <InputLabel id="demo-simple-select-label">
                                {generalT("branch")}
                              </InputLabel>
                              <Controller
                                name={`items.${index}.branch_id`}
                                control={control}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    key={field.value}
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    label={generalT("branch")}
                                  >
                                    {branchesHavePermission?.map(
                                      ({ id, name, active_subscriptions }) => {
                                        return (
                                          <MenuItem
                                            key={id}
                                            value={`${id}`}
                                            disabled={
                                              !active_subscriptions?.package
                                                .has_items
                                            }
                                          >
                                            {name}
                                          </MenuItem>
                                        );
                                      },
                                    )}
                                  </Select>
                                )}
                              />
                              <FormHelperText id="my-helper-text">
                                {generalT(
                                  `${errors.items?.[index]?.branch_id?.message ?? ""}`,
                                )}
                              </FormHelperText>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            {/* tax rate */}
                            <Controller
                              name={`items.${index}.tax_rate`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={t("taxRate")}
                                  variant="outlined"
                                  {...field}
                                  error={!!errors.items?.[index]?.tax_rate}
                                  helperText={
                                    errors.items?.[index]?.tax_rate?.message
                                  }
                                  fullWidth
                                  size="small"
                                  //   sx={{ minWidth: 170, width: 170 }}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Typography fontSize={"14px"}>
                                          %
                                        </Typography>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Controller
                              name={`items.${index}.quantity`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={t("qnty")}
                                  variant="outlined"
                                  {...field}
                                  error={!!errors.items?.[index]?.quantity}
                                  helperText={
                                    errors.items?.[index]?.quantity?.message
                                  }
                                  fullWidth
                                  size="small"
                                  //   sx={{ minWidth: 170, width: 170 }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Controller
                              name={`items.${index}.price`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={t("price")}
                                  variant="outlined"
                                  {...field}
                                  error={!!errors.items?.[index]?.price}
                                  helperText={
                                    errors.items?.[index]?.price?.message
                                  }
                                  fullWidth
                                  size="small"
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {/* amount */}
                            {amount}
                          </TableCell>
                          <TableCell align="center">
                            <Controller
                              name={`items.${index}.discount_percentage`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  type="number"
                                  label={generalT("discount")}
                                  variant="outlined"
                                  {...field}
                                  error={
                                    !!errors.items?.[index]?.discount_percentage
                                  }
                                  helperText={
                                    errors.items?.[index]?.discount_percentage
                                      ?.message
                                  }
                                  fullWidth
                                  size="small"
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center">{tax}</TableCell>
                          <TableCell align="center">{total}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleDeleteItem(index);
                              }}
                            >
                              <img
                                src={DeleteIcon}
                                alt=""
                                width={"16px"}
                                style={{ cursor: "pointer" }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {true ? (
                <Autocomplete
                  size="small"
                  disablePortal
                  id="combo-box-demo"
                  options={
                    itemsData?.data.filter(
                      (item: any) => !selectedItemsIds?.includes(item.id),
                    ) ?? []
                  }
                  sx={{ width: 255 }}
                  getOptionLabel={(option: any) => option.name}
                  getOptionKey={(option) => `${option.id}`}
                  value={selectedItem}
                  onChange={handleItemSelect}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  renderInput={(params: any) => (
                    <TextField {...params} label={t("searchAndAddItem")} />
                  )}
                />
              ) : null}
              {/* total summary */}
              <Stack p={2} gap={1.5} bgcolor={"#F9FAFB"}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("subtotal")}
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {subTotal}
                  </Typography>
                </Stack>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("discount")}
                  </Typography>
                  <Stack direction={"row"} gap={2} alignItems={"center"}>
                    {/* global discount */}
                    <Controller
                      name="discount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          type="number"
                          fullWidth
                          label={""}
                          size="small"
                          {...field}
                          error={!!errors.discount}
                          helperText={errors.discount?.message}
                        />
                      )}
                    />
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.is_discount_percentage}
                      sx={{ maxWidth: "80px" }}
                    >
                      <Controller
                        name="is_discount_percentage"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            key={field.value}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label={""}
                          >
                            <MenuItem value={"1"}>{"%"}</MenuItem>
                            <MenuItem value={"0"}>{"SAR"}</MenuItem>
                          </Select>
                        )}
                      />
                      <FormHelperText id="my-helper-text">
                        {generalT(
                          `${errors.is_discount_percentage?.message ?? ""}`,
                        )}
                      </FormHelperText>
                    </FormControl>
                    <Typography fontSize={"14px"} color={"#344054"}>
                      {allDiscount}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {generalT("vat")}
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {allTax}
                  </Typography>
                </Stack>
                <Box height={"1px"} bgcolor={"#D0D5DD"} />
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Typography
                    fontSize={"14px"}
                    fontWeight={500}
                    color={"#344054"}
                  >
                    {generalT("total")}
                  </Typography>
                  <Typography fontSize={"14px"} color={"#344054"}>
                    {allTotalAfterDiscount}
                  </Typography>
                </Stack>
              </Stack>

              {/* payments */}
              {!isToAddCreditNote &&
                paymentsFields.map((paymentField, index) => {
                  return (
                    <Stack key={paymentField.id} direction={"row"} gap={1}>
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          errors.payments &&
                          !!errors.payments[index]?.payment_method_id
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          {t("paymentMethodOptional")}
                        </InputLabel>
                        <Controller
                          name={`payments.${index}.payment_method_id`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              key={field.value}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              label={t("paymentMethodOptional")}
                              disabled={mode === "edit"}
                            >
                              {paymentsData?.data
                                ?.filter((pay) => pay.status)
                                .map(({ id, name }) => {
                                  return (
                                    <MenuItem key={id} value={`${id}`}>
                                      {name}
                                    </MenuItem>
                                  );
                                })}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.payments?.[index]?.payment_method_id?.message}
                        </FormHelperText>
                      </FormControl>

                      <Controller
                        name={`payments.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            label={t("amount")}
                            variant="outlined"
                            {...field}
                            error={!!errors.payments?.[index]?.amount}
                            helperText={
                              errors.payments?.[index]?.amount?.message
                            }
                            fullWidth
                            size="small"
                            InputProps={{
                              inputProps: { min: 0 },
                            }}
                            disabled={mode === "edit"}
                          />
                        )}
                      />

                      {mode === "add" && (
                        <IconButton onClick={() => handleRemovePayment(index)}>
                          <img src={DeleteIcon} alt="" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}
              {!isToAddCreditNote && (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  {mode === "add" && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<img src={AddMoreIcon} alt="" />}
                      onClick={handleAppendPayment}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {t("addAnotherMethod")}
                    </Button>
                  )}

                  <Typography fontSize={"14px"}>
                    {t("credit")}: {credit} {generalT("sar")}
                  </Typography>
                </Stack>
              )}

              {/* note */}
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={generalT("notesOptional")}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    maxRows={3}
                    {...field}
                    error={!!errors.note}
                    helperText={errors.note?.message}
                  />
                )}
              />

              <Typography>{salesT("terms")}</Typography>

              <FormControl fullWidth size="small" error={!!errors.term_id}>
                <Controller
                  name="term_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      key={field.value}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      label={""}
                    >
                      {optionalTerms?.map(({ id, title }: any) => {
                        return (
                          <MenuItem key={id} value={id}>
                            {title}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  )}
                />
                <FormHelperText id="my-helper-text">
                  {generalT(`${errors.term_id?.message ?? ""}`)}
                </FormHelperText>
              </FormControl>

              <Stack px={4} py={1.5} gap={1.5} bgcolor={"#F9FAFB"}>
                {mandatoryTerms?.map((term) => (
                  <Typography component={"li"} key={term?.id} fontSize={"14px"}>
                    {term.title}
                  </Typography>
                ))}
              </Stack>

              {/* files */}
              {mode === "add" && (
                <Box width={"100%"} mt={2}>
                  <FilePondUploader
                    onUpload={(e) => setFiles(e)}
                    maxFiles={3}
                    maxFileSize={3}
                    acceptedFileTypes={[
                      "image/*",
                      "application/pdf",
                      "text/csv",
                      "application/vnd.ms-excel",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    ]}
                  />
                </Box>
              )}
            </Stack>

            {true ? (
              <Stack
                spacing={2}
                direction={"row"}
                mt={3}
                justifyContent={"flex-end"}
              >
                <Button
                  variant="outlined"
                  color="tertiary"
                  onClick={navigateToInvoices}
                  sx={{ minWidth: "180px" }}
                >
                  {generalT("back")}
                </Button>
                {invoiceData?.data?.invoice_status !== "cancelled" ? (
                  <>
                    {!invoiceData ||
                    invoiceData?.data?.invoice_status === "draft" ? (
                      <LoadingButton
                        variant="contained"
                        type="submit"
                        loading={isPending || isPendingUpdate || isFetching}
                        sx={{ minWidth: "180px" }}
                        disabled={watch("items")?.length === 0}
                        onClick={() => setSaveAsDraft(true)}
                      >
                        {generalT("saveAsDraft")}
                      </LoadingButton>
                    ) : null}

                    {true && (
                      <LoadingButton
                        variant="contained"
                        type="submit"
                        loading={isPending || isPendingUpdate || isFetching}
                        sx={{ minWidth: "180px" }}
                        disabled={watch("items")?.length === 0}
                        onClick={() => setSaveAsDraft(false)}
                      >
                        {isToCancelAndCopy
                          ? salesT("cancelAndCopy")
                          : generalT("submit")}
                      </LoadingButton>
                    )}
                  </>
                ) : null}
              </Stack>
            ) : null}
          </form>
        </>
      )}
      {/* {openImportRecipe ? (
        <ImportRecipePopup
          open={openImportRecipe}
          handleClose={handleCloseImportRecipe}
          refetch={refetch}
        />
      ) : null} */}
    </Stack>
  );
};

export default AddEditInvoice;
